import { db } from '@/models/db';
import type { DeltaPacketRecord } from '@/models/hydrate.ts';
import { Confirmation } from '@/models/registry.ts';
import type { BaseStore } from '@/models/stores/base.ts';
import {
  ConfirmationField,
  ConfirmationTargetType,
  type Confirmation as ConfirmationType,
} from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import { dealStore } from './deals';

class ConfirmationsStore implements BaseStore<Confirmation, ConfirmationType> {
  store: ObservableMap<string, Confirmation> = observable.map();

  // Indexes for different target types, targetType -> (targetId -> (fieldKey -> Confirmation))
  storeIndexedByDealId: ObservableMap<
    string,
    ObservableMap<ConfirmationField, Confirmation>
  > = observable.map();
  storeIndexedByTradeId: ObservableMap<
    string,
    ObservableMap<ConfirmationField, Confirmation>
  > = observable.map();

  private typeToIndexMap = {
    [ConfirmationTargetType.Deal]: this.storeIndexedByDealId,
    [ConfirmationTargetType.Trade]: this.storeIndexedByTradeId,
  };

  constructor() {
    makeAutoObservable(
      this,
      {
        store: observable.shallow,
        storeIndexedByDealId: observable.shallow,
        storeIndexedByTradeId: observable.shallow,
      },
      { autoBind: true },
    );
  }

  get all(): Confirmation[] {
    return Array.from(this.store.values());
  }

  getById(id: string): Confirmation | undefined {
    return this.store.get(id);
  }

  private setConfirmationField = <K extends keyof ConfirmationType>(
    confirmation: Confirmation,
    key: K,
    value: ConfirmationType[K],
  ) => {
    (confirmation as Pick<Confirmation, K>)[key] = value as Confirmation[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ConfirmationType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.confirmations.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.confirmations.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.confirmations.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.confirmations.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<ConfirmationType>;
  }) {
    const existingConfirmation = this.getById(delta.record_id);
    if (!existingConfirmation) return;

    const entries = Object.entries(delta.data) as [
      keyof ConfirmationType,
      ConfirmationType[keyof ConfirmationType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingConfirmation[key] === newValue) {
        continue;
      }

      this.setConfirmationField(existingConfirmation, key, newValue);
    }
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<ConfirmationType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ConfirmationType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.confirmations.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.remove(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  private getOrCreateFieldMap(
    index: ObservableMap<
      string,
      ObservableMap<ConfirmationField, Confirmation>
    >,
    key: string,
  ) {
    let map = index.get(key);
    if (!map) {
      map = observable.map<ConfirmationField, Confirmation>();
      index.set(key, map);
    }
    return map;
  }

  getByTargetAndField(
    targetId: string,
    targetType: ConfirmationTargetType,
    field: ConfirmationField,
  ): Confirmation | undefined {
    const indexMap = this.typeToIndexMap[targetType];
    if (!indexMap) return;
    const fieldMap = this.getOrCreateFieldMap(indexMap, targetId);
    return fieldMap.get(field);
  }

  private setOne(confirmation: Confirmation) {
    if (this.store.has(confirmation.id)) {
      return;
    }

    this.store.set(confirmation.id, confirmation);
    const indexMap = this.typeToIndexMap[confirmation.confirmation_to_type];
    const fieldMap = this.getOrCreateFieldMap(
      indexMap,
      confirmation.confirmation_to_id,
    );
    fieldMap.set(confirmation.field_key, confirmation);
  }

  private removeOne(confirmation: Confirmation) {
    this.store.delete(confirmation.id);

    const indexMap = this.typeToIndexMap[confirmation.confirmation_to_type];
    const fieldMap = this.getOrCreateFieldMap(
      indexMap,
      confirmation.confirmation_to_id,
    );
    if (fieldMap.get(confirmation.field_key)?.id === confirmation.id) {
      fieldMap.delete(confirmation.field_key);
    }

    db.confirmations.delete(confirmation.id);
  }

  add(confirmation: ConfirmationType) {
    const newConfirmation = new Confirmation(confirmation);
    this.setOne(newConfirmation);
  }

  upsert(confirmation: ConfirmationType) {
    const existing = this.getByTargetAndField(
      confirmation.confirmation_to_id,
      confirmation.confirmation_to_type,
      confirmation.field_key,
    );

    if (existing) {
      this.store.delete(existing.id);
      existing.id = confirmation.id;
      existing.most_recent_entry_id = confirmation.most_recent_entry_id;
      existing.confirmed = confirmation.confirmed;
      existing.is_manually_toggled = confirmation.is_manually_toggled;
      this.store.set(existing.id, existing);
      const indexMap = this.typeToIndexMap[confirmation.confirmation_to_type];
      const fieldMap = this.getOrCreateFieldMap(
        indexMap,
        confirmation.confirmation_to_id,
      );
      fieldMap.set(confirmation.field_key, existing);
    } else {
      this.add(confirmation);
    }
  }

  remove(id: ConfirmationType['id']) {
    const confirmation = this.store.get(id);
    if (!confirmation) return;
    this.removeOne(confirmation);
  }

  setConfirmed({
    id,
    targetId,
    targetType,
    field,
  }: {
    id: string;
    targetId: string;
    targetType: ConfirmationTargetType;
    field: ConfirmationField;
  }) {
    let confirmation = this.getByTargetAndField(targetId, targetType, field);
    if (!confirmation) {
      confirmation = new Confirmation({
        id,
        confirmation_to_id: targetId,
        confirmation_to_type: targetType,
        field_key: field,
        confirmed: false,
        most_recent_entry_id: '',
        is_manually_toggled: false,
      });
      this.setOne(confirmation);
    }

    confirmation.confirmed = true;
  }

  clearConfirmed(
    targetId: string,
    targetType: ConfirmationTargetType,
    field: ConfirmationField,
  ) {
    let confirmation = this.getByTargetAndField(targetId, targetType, field);
    if (!confirmation) {
      return;
    }
    confirmation.confirmed = false;
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'confirmations',
      entity: 'confirmations',
      modelStore: confirmationsStore,
    });
  }

  async hydrateOnlyForNewDeals(): Promise<void> {
    const newDeals = dealStore.newDeals;

    const confirmationIds: string[] = [];
    for (const deal of newDeals) {
      const confirmations = this.storeIndexedByDealId.get(deal.id);
      if (confirmations) {
        confirmations.forEach((confirmation) =>
          confirmationIds.push(confirmation.id),
        );
      }
    }

    await hydrateFromWorker({
      tableName: 'confirmations',
      entity: 'confirmations',
      modelStore: confirmationsStore,
      ids: confirmationIds,
    });
  }

  async hydrateDeal(
    dealId: ConfirmationType['confirmation_to_id'],
  ): Promise<void> {
    const items = await db.confirmations.toArray();
    items.forEach((item) => {
      if (
        item.confirmation_to_id === dealId &&
        item.confirmation_to_type === ConfirmationTargetType.Deal
      )
        this.upsert(item);
    });
  }
}

export const confirmationsStore = new ConfirmationsStore();
